<template>
  <div class="tabulation-report">
    <!-- Control Buttons -->
    <div class="no-print control-bar">
      <h4 class="mb-0">Marks Record Print</h4>
      <div>
        <b-button variant="primary" @click="print" size="sm">
          <feather-icon icon="PrinterIcon" size="16" class="mr-50" />
          Print
        </b-button>
        <b-button variant="outline-secondary" @click="close" size="sm" class="ml-2">
          <feather-icon icon="XIcon" size="16" class="mr-50" />
          Close
        </b-button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem;" />
      <p class="mt-3 font-weight-medium">{{ loadingText }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h5 class="text-danger font-weight-medium">{{ error }}</h5>
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Header -->
      <div class="page-header">
        <h3 class="school-name">{{ schoolName }}</h3>
        <h5 class="report-title">Marks Record Print</h5>
        <div class="header-meta">
          <span><strong>Class:</strong> {{ reportInfo.className }} - {{ reportInfo.sectionName }}</span>
          <span class="separator">|</span>
          <span><strong>Exam:</strong> {{ reportInfo.examTypeName }}</span>
          <span class="separator">|</span>
          <span><strong>Session:</strong> {{ reportInfo.sessionName }}</span>
        </div>
      </div>

      <!-- Students Table -->
      <table v-if="studentsData.length > 0" class="students-table">
        <tbody v-for="(student, studentIndex) in studentsData" :key="'student-' + student.studentID" class="student-group">
          <!-- Student Info Row -->
          <tr class="student-info-row">
            <td class="sno-cell">{{ studentIndex + 1 }}</td>
            <td class="name-cell">{{ student.studentName }}</td>
            <td class="gr-cell">{{ student.grNo }}</td>
            <td class="father-cell">{{ student.fatherName }}</td>
          </tr>

          <!-- Subjects Row -->
          <tr class="subjects-row">
            <td colspan="4" class="subjects-cell">
              <div v-for="subject in student.subjects" :key="subject.subjectId" class="subject-container">
                <div class="subject-name-bar">{{ subject.subjectName }}</div>
                
                <!-- Rating-Based (Icon-Base) Display -->
                <div v-if="subject.sheetType === 'icon-base'" class="rating-display">
                  <table class="rating-table">
                    <thead>
                      <tr>
                        <th width="40">#</th>
                        <th>Category</th>
                        <th>Skill / Option</th>
                        <th width="100" class="text-center">Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, idx) in subject.marks.marksData" :key="item.id">
                        <td class="text-center">{{ idx + 1 }}</td>
                        <td class="category-name">{{ item.categoryName }}</td>
                        <td>{{ item.optionName }}</td>
                        <td class="text-center">
                          <span class="rating-badge" :class="'rating-' + (item.rating || 'na').toLowerCase()">
                            {{ capitalizeRating(item.rating) }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Marks-Based (Questions-Base) Display -->
                <div v-else-if="subject.marks" class="marks-display">
                  <table class="marks-table">
                    <thead>
                      <tr>
                        <th width="40">#</th>
                        <th>Question</th>
                        <th width="100" class="text-center">Total Marks</th>
                        <th width="100" class="text-center">Obtained</th>
                        <th width="80" class="text-center">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(question, qIdx) in (subject.marks.marksData || [])" :key="question.id">
                        <td class="text-center">{{ qIdx + 1 }}</td>
                        <td>{{ question.questionNumber }}</td>
                        <td class="text-center">{{ question.questionTotalMarks }}</td>
                        <td class="text-center font-weight-bold">{{ question.obtainedMarks }}</td>
                        <td class="text-center">{{ calculatePercentage(question.obtainedMarks, question.questionTotalMarks) }}%</td>
                      </tr>
                      <tr class="total-row">
                        <td colspan="2" class="text-right font-weight-bold">TOTAL</td>
                        <td class="text-center font-weight-bold">{{ subject.marks.totalMarks || 0 }}</td>
                        <td class="text-center font-weight-bold">{{ calculateTotalObtained(subject.marks.marksData) }}</td>
                        <td class="text-center font-weight-bold"></td>
                      </tr>
                    </tbody>
                  </table>
                  
                  <!-- Summary Info -->
                  <div v-if="subject.marks.marks" class="summary-row">
                    <span><strong>Grade:</strong> <span class="badge badge-primary">{{ subject.marks.marks.meta_sub_grade || 'N/A' }}</span></span>
                    <span><strong>Rank:</strong> {{ subject.marks.marks.rank || 'N/A' }}</span>
                    <span><strong>Status:</strong> <span :class="subject.marks.marks.status === 'PASSED' ? 'text-success' : 'text-danger'">{{ subject.marks.marks.status || 'N/A' }}</span></span>
                    <span><strong>Highest:</strong> {{ subject.marks.marks.meta_highest || 0 }}</span>
                    <span><strong>Average:</strong> {{ subject.marks.marks.meta_average || 0 }}</span>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- No Data -->
      <div v-else class="text-center py-5">
        <h6 class="text-muted">No student data available</h6>
      </div>

      <!-- Footer -->
      <div class="page-footer">
        <small><strong>Generated:</strong> {{ currentDateTime }} | <strong>IUSS</strong> - Excellence in Education</small>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue'
import axios from 'axios'

export default {
  name: 'MarksRecordPrint',
  components: {
    BButton,
    BSpinner
  },
  data() {
    return {
      loading: false,
      loadingText: 'Loading marks record...',
      error: null,
      studentsData: [],
      reportInfo: {
        className: '',
        sectionName: '',
        examTypeName: '',
        sessionName: ''
      }
    }
  },
  computed: {
    schoolName() {
      const name =
        this.$store.state.branchName ||
        this.$store.state.userData?.campusName ||
        this.$store.state.userData?.branch ||
        ''
      if (name) return String(name).toUpperCase()
      const report = String(this.$store.state.report || '')
        .replace(/_/g, '')
        .replace(/^myskool/i, '')
        .toUpperCase()
      return report || 'SCHOOL'
    },
    currentDateTime() {
      const now = new Date()
      return now.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  },
  created() {
    this.loadReportData()
  },
  methods: {
    async loadReportData() {
      try {
        this.loading = true
        this.error = null

        const { db, cID, classId, sectionId, examTypeId, sessionId, subjectIds } = this.$route.query

        // Fetch header names concurrently with report data
        this.fetchHeaderNames({ db, cID, classId, sectionId, examTypeId, sessionId })

        const url = `${this.$store.state.domain}TabulationSheetMarks/GetTabulationSheetReport`
        
        const response = await axios.get(url, {
          params: {
            db: db,
            cID: cID,
            classId: classId,
            sectionId: sectionId,
            examTypeId: examTypeId,
            sessionId: sessionId,
            subjectIds: subjectIds
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`
          }
        })

        if (response.data) {
          const responseData = response.data.data || response.data
          
          if (Array.isArray(responseData)) {
            this.studentsData = responseData
          } else {
            this.studentsData = []
          }
        } else {
          this.studentsData = []
        }
      } catch (error) {
        console.error('Failed to load report data:', error)
        this.error = error.response?.data?.message || 'Failed to load marks record report. Please try again.'
      } finally {
        this.loading = false
      }
    },

    async fetchHeaderNames(params) {
      const { db, cID, classId, sectionId, examTypeId, sessionId } = params
      const token = this.$store.state.userData.token
      const headers = { Authorization: `Bearer ${token}` }

      try {
        const [classesRes, sectionsRes, examsRes, sessionsRes] = await Promise.all([
          axios.get(`${this.$store.state.domain}myclasses/LoadClasses`, { params: { db, cId: cID, dID: 0 }, headers }).catch(() => null),
          axios.get(`${this.$store.state.domain}Sections`, { params: { db, cID, classID: classId }, headers }).catch(() => null),
          axios.get(`${this.$store.state.domain}ExamTypes/LoadData`, { params: { db, cID, status: 'active', page: 1, pageSize: 1000 }, headers }).catch(() => null),
          axios.get(`${this.$store.state.domain}Sessions`, { params: { db, cID }, headers }).catch(() => null)
        ])

        // Process Classes
        if (classesRes) {
          const classes = classesRes.data?.data || classesRes.data || []
          const classObj = classes.find(c => (c.cls?.id || c.id) == classId)
          if (classObj) {
            this.reportInfo.className = classObj.cls?.name || classObj.name || 'N/A'
          }
        }

        // Process Sections
        if (sectionsRes) {
          const sections = sectionsRes.data?.data || sectionsRes.data || []
          const sectionObj = sections.find(s => (s.id || s.sectionID) == sectionId)
          if (sectionObj) {
            this.reportInfo.sectionName = sectionObj.name || sectionObj.section || 'N/A'
          }
        }

        // Process Exams
        if (examsRes) {
          const exams = examsRes.data?.data || examsRes.data || []
          const examObj = exams.find(e => (e.id || e.examTypeID) == examTypeId)
          if (examObj) {
            this.reportInfo.examTypeName = examObj.examType || examObj.name || 'N/A'
          }
        }

        // Process Sessions
        if (sessionsRes) {
          const sessions = sessionsRes.data?.data || sessionsRes.data || []
          const sessionObj = sessions.find(s => s.id == sessionId)
          if (sessionObj) {
            this.reportInfo.sessionName = sessionObj.session || sessionObj.name || 'N/A'
          }
        }
      } catch (err) {
        console.error('Error fetching header names:', err)
      }
    },

    calculatePercentage(obtained, total) {
      if (!total || total === 0) return '0.00'
      return ((obtained / total) * 100).toFixed(2)
    },

    calculateTotalObtained(marksData) {
      if (!marksData || !Array.isArray(marksData)) return 0
      return marksData.reduce((sum, item) => {
        return sum + (parseFloat(item.obtainedMarks) || 0)
      }, 0)
    },

    capitalizeRating(rating) {
      if (!rating || rating === null || rating === undefined) return 'N/A'
      const ratingStr = String(rating)
      return ratingStr.charAt(0).toUpperCase() + ratingStr.slice(1).toLowerCase()
    },

    print() {
      window.print()
    },

    close() {
      this.$router.go(-1)
    }
  }
}
</script>

<style lang="scss" scoped>
* {
  font-weight: 500;
}

.tabulation-report {
  background: #f0f0f0;
  min-height: 100vh;
  padding: 15px;
}

.control-bar {
  background: white;
  padding: 12px 20px;
  margin-bottom: 15px;
  border: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h4 {
    color: #000;
    font-weight: 600;
  }
}

.report-page {
  background: white;
  padding: 20px;
  border: 1px solid #ccc;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.page-header {
  text-align: center;
  border-bottom: 2px solid #000;
  padding-bottom: 12px;
  margin-bottom: 15px;
  
  .school-name {
    font-size: 1.4rem;
    font-weight: 700;
    color: #000;
    margin-bottom: 5px;
  }
  
  .report-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }
  
  .header-meta {
    font-size: 0.9rem;
    color: #000;
    font-weight: 500;
    
    .separator {
      margin: 0 10px;
      color: #999;
    }
  }
}

.students-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 15px;
  
  .student-info-row {
    background: #e8e8e8;
    border: 1px solid #999;
    
    td {
      padding: 8px 10px;
      color: #000;
      font-weight: 600;
      border-right: 1px solid #999;
      
      &:last-child {
        border-right: none;
      }
    }
    
    .sno-cell {
      width: 40px;
      text-align: center;
    }
    
    .name-cell {
      width: 30%;
    }
    
    .gr-cell {
      width: 20%;
    }
    
    .father-cell {
      width: 30%;
    }
  }
  
  .subjects-row {
    .subjects-cell {
      padding: 0;
      background: white;
    }
  }
}

.subject-container {
  border: 1px solid #ccc;
  border-top: none;
  margin-bottom: 0;
}

.subject-name-bar {
  background: #d0d0d0;
  padding: 6px 12px;
  font-weight: 700;
  color: #000;
  font-size: 0.95rem;
  border-bottom: 1px solid #999;
}

.rating-display,
.marks-display {
  padding: 10px;
}

.rating-table,
.marks-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  
  thead {
    background: #f5f5f5;
    
    th {
      padding: 6px 8px;
      border: 1px solid #ccc;
      color: #000;
      font-weight: 700;
      text-align: left;
      font-size: 0.8rem;
    }
  }
  
  tbody {
    tr {
      border-bottom: 1px solid #e0e0e0;
      
      td {
        padding: 5px 8px;
        color: #000;
        font-weight: 500;
        border-left: 1px solid #e0e0e0;
        border-right: 1px solid #e0e0e0;
      }
      
      &.total-row {
        background: #f0f0f0;
        font-weight: 700;
        border-top: 2px solid #999;
        
        td {
          padding: 7px 8px;
          font-size: 0.9rem;
        }
      }
    }
  }
}

.category-name {
  font-weight: 600;
  color: #333;
}

.rating-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 3px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  
  &.rating-excellent {
    background: #28a745;
    color: white;
  }
  
  &.rating-good {
    background: #17a2b8;
    color: white;
  }
  
  &.rating-satisfactory {
    background: #ffc107;
    color: #000;
  }
  
  &.rating-needsimprovement,
  &.rating-needs-improvement {
    background: #dc3545;
    color: white;
  }
}

.summary-row {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  padding: 8px 0;
  margin-top: 8px;
  font-size: 0.85rem;
  color: #000;
  border-top: 1px solid #ccc;
  
  span {
    font-weight: 500;
    
    strong {
      font-weight: 700;
    }
  }
  
  .badge {
    font-weight: 700;
  }
}

.page-footer {
  text-align: center;
  padding-top: 12px;
  margin-top: 15px;
  border-top: 1px solid #ccc;
  color: #666;
}

@media print {
  .no-print {
    display: none !important;
  }
  
  .tabulation-report {
    background: white;
    padding: 0;
    min-height: auto;
  }
  
  .report-page {
    box-shadow: none;
    border: none;
    padding: 0;
    margin: 0;
  }
  
  .page-header {
    margin-bottom: 10px;
    padding-bottom: 8px;
  }

  .students-table {
    font-size: 9pt;
    page-break-inside: auto;
  }
  
  .student-group {
    page-break-inside: auto;
  }

  .subject-container {
    page-break-inside: auto;
    margin-bottom: 5px;
  }
  
  .subject-name-bar {
    page-break-after: avoid;
    font-size: 8.5pt;
    padding: 4px 10px;
  }
  
  .student-info-row {
    page-break-after: avoid;
  }
  
  .subjects-row {
    page-break-inside: auto;
  }

  .rating-table thead, .marks-table thead {
    display: table-header-group;
  }

  .rating-table th, .rating-table td,
  .marks-table th, .marks-table td {
    padding: 3px 5px !important;
  }
  
  tr {
    page-break-inside: avoid;
    page-break-after: auto;
  }
}

@media (max-width: 768px) {
  .header-meta {
    display: flex;
    flex-direction: column;
    gap: 3px;
    
    .separator {
      display: none;
    }
  }
}
</style>
